import type { AnnounceDetail, Event as GQLItem } from '../../../generated/api.graphql';
import { take, thawApolloResult } from '../../../util';
import * as Venue from '../Venue/Venue';
import * as VenueRoom from '../Venue/VenueRoom';
import * as EventGroup from './EventGroup';
import type { EventState } from './EventState';

/**
 * The single event the event modals render -- announce, event edit, the ticket-counts report
 * and the Prism connector all read this one record off the admin's event store.
 *
 * This is the **second** mapper in this directory bound to the schema's `Event` type, in
 * tension with the one-type-one-mapper rule in `doc/claude/api-layer.md`, and the same
 * deliberate deviation `App/Artist/Artist.ts` carries. `Event.ts` stays the thin identity
 * slice the order detail reads; the detail scalars could have been added to it harmlessly,
 * but its `announceDetail` is the 3-field `EventAnnounceSummary`, and re-typing that field is
 * what the modals need. Two mappers beat one field with two meanings. Grow this one when the
 * event surface consolidates; don't fork a third.
 *
 * `announceDetail` is deliberately **not** mapped. `App.Announce.Api.saveAnnounceDetail`
 * resolves the generated `AnnounceDetail` unmapped and the admin store spreads it straight
 * into this same field, so mapping it here would hand the announce modal one shape on load
 * and another after a save. Note the consequence: its list-valued children arrive as `null`
 * when the record has none, where the retired legacy mapper defaulted them to `[]`. The
 * modal's own `|| []` guards cover that; `EventDetail.spec.ts` pins it so they stay visible.
 *
 * It is **deep**-copied, not passed by reference, because Apollo hands back a frozen graph.
 * See `thawApolloResult` for why the whole subtree has to be thawed rather than just the
 * record: the store's own `buttonTextMode` / `ticketsUrlMode` writes are top-level and a
 * shallow copy would carry them, but the announce modal feeds this record to vee-validate,
 * whose deep `isEqual` throws a proxy-invariant `TypeError` the moment it walks a frozen
 * array of objects. `eventArtists` is exactly that, and it is the one announce list the
 * modal does not re-map, so every event with an artist crashed the modal on open.
 *
 * The retired legacy mapper never hit this: it rebuilt each child list through its own
 * `fromGql`, so the copy was incidental but total. Keep this one total too.
 */
export type EventDetail = {
  id: number;
  name: string;
  fullSlug?: string;
  state: EventState;
  date: string;
  doorTime?: string;
  startTime?: string;
  endTime?: string;
  timezone?: string;
  availableForSalesTax?: boolean;
  availableForProcessingFee?: boolean;
  availableForDonation?: boolean;
  donationDescription?: string;
  donationButtonText?: string;
  mailPdfTickets?: boolean;
  sendWillCallReminders?: boolean;
  minimumAge?: number;
  confirmationEmailContent?: string;
  footerContent?: string;
  orderConfirmationContent?: string;
  ticketLimit?: number;
  connectedToPrism?: boolean;
  prismEventId?: number;
  canAddSections?: boolean;
  /** Widened to the whole `Venue` for `currency`, as `Event.ts`'s does. */
  venue?: Venue.Venue;
  venueRoom?: VenueRoom.VenueRoom;
  eventGroup?: EventGroup.EventGroup;
  announceDetail?: AnnounceDetail;
};

export const fromGql = (item: GQLItem): EventDetail => ({
  ...take<EventDetail>(
    item,
    'id',
    'name',
    'fullSlug',
    'state',
    'date',
    'doorTime',
    'startTime',
    'endTime',
    'timezone',
    'availableForSalesTax',
    'availableForProcessingFee',
    'availableForDonation',
    'donationDescription',
    'donationButtonText',
    'mailPdfTickets',
    'sendWillCallReminders',
    'minimumAge',
    'confirmationEmailContent',
    'footerContent',
    'orderConfirmationContent',
    'ticketLimit',
    'connectedToPrism',
    'prismEventId',
    'canAddSections',
  ),
  venue: item.venue ? Venue.fromGql(item.venue) : undefined,
  venueRoom: item.venueRoom ? VenueRoom.fromGql(item.venueRoom) : undefined,
  eventGroup: item.eventGroup ? EventGroup.fromGql(item.eventGroup) : undefined,
  announceDetail: item.announceDetail ? thawApolloResult(item.announceDetail) : undefined,
});
